import { useEffect } from 'react';

import { selectErrorCode } from 'selector/axiosSelector';
import { datadogLogs } from '@datadog/browser-logs';
import { getAppConfig } from 'app/getConfig';
import { APP_VERSION } from 'config/appVersion';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { AxiosError } from 'axios';
import { FeatureFlags } from 'config/growthbook/featureFlags';
import { latestReduxActions, wrappedReduxStateSnapshot } from 'shared/constants/datadog';

export const useInitializeDataDogLogs = () => {
  const isRUMEnabled = useFeatureIsOn(FeatureFlags.DATADOG_LOGS_FE);
  const { DATADOG_SURVEY_UI_CLIENT_TOKEN, NODE_ENV } = getAppConfig();

  const isAllowedToInit = isRUMEnabled && DATADOG_SURVEY_UI_CLIENT_TOKEN;

  useEffect(() => {
    if (isAllowedToInit) {
      datadogLogs.init({
        clientToken: DATADOG_SURVEY_UI_CLIENT_TOKEN,
        site: 'datadoghq.eu',
        service: 'survey-ui',
        env: NODE_ENV,
        version: APP_VERSION,
        sessionSampleRate: 100,
        forwardErrorsToLogs: true,
        // Only forward console errors — 'all' risks leaking anything logged via console.log.
        forwardConsoleLogs: ['error'],
        // Withhold data until the respondent grants tracking consent. The SDK buffers events
        // and only flushes once CookieConsent calls setTrackingConsent('granted'); if consent
        // is refused, nothing is sent. Mirrors the RUM pipeline.
        trackingConsent: 'not-granted',
      });
    } else {
      datadogLogs.logger.error('datadogLogs failed to init', {
        env: NODE_ENV,
        service: 'survey-ui',
        DATADOG_SURVEY_UI_CLIENT_TOKEN: !!DATADOG_SURVEY_UI_CLIENT_TOKEN,
        isRUMEnabled,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

/**
 * Drop `accessCode` from the Redux snapshot before logging — it holds respondent PII
 * (employeeEmail, employeeId, access `code`, surveyLink) that must never reach Datadog.
 */
const scrubReduxState = (state: typeof wrappedReduxStateSnapshot.state) => {
  if (!state) {
    return state;
  }

  const { accessCode, ...safeState } = state;

  return safeState;
};

/**
 * Reduce an AxiosError to non-sensitive fields. The raw error carries request/response
 * bodies, headers and the request URL (which can embed the access code), so we keep only
 * the error category needed for debugging.
 */
const sanitizeAxiosError = (error: AxiosError) => ({
  name: error.name,
  message: error.message,
  code: error.code,
  status: error.response?.status,
  method: error.config?.method,
});

export const logInterceptedError = (error: AxiosError) => {
  const errorCode = selectErrorCode(error);

  datadogLogs.logger.error(`[Axios] ${errorCode} Error Intercepted`, {
    // Only action types — payloads can carry respondent PII (access code, email, answers).
    latestReduxActions: [...latestReduxActions].reverse().map(({ type }) => type),
    error: sanitizeAxiosError(error),
    state: scrubReduxState(wrappedReduxStateSnapshot.state),
  });
};
